import {
  CarrierType,
  CarrierVisitCraneDto,
  CarrierVisitStatus,
  CarrierVisitWorkQueueDto,
  Location,
  LocationDto,
  WorkInstructionControlRoomDto,
  WorkInstructionControlRoomJobDto,
  WorkInstructionStatus,
  WorkQueueStatus,
} from '@operations/app/api'
import { WorkInstructionUpsertedEventPayload } from '@operations/app/api/signalRDtos/workInstructionUpsertedEventPayload'
import { calculateEndTime } from '@operations/features/craneSplit/stores/crane-split-container.ui-store'
import dayjs from 'dayjs'
import { map } from 'lodash'
import {
  CarrierVisitCraneViewModel,
  CarrierVisitViewModel,
  CarrierVisitWorkQueueViewModel,
  ControlRoomWorkTabType,
  WorkInstructionViewModel,
} from '../models/work-instruction-view.model'

export const isCarrierTypeMatchingTab = (
  carrierType: CarrierType,
  tabType: ControlRoomWorkTabType,
) => {
  switch (tabType) {
    case 'vessel':
      return carrierType === 'Vessel'
    case 'train':
      return carrierType === 'Train'
    default:
      return false
  }
}

export const mapWorkTabTypeToCarrierType = (
  tabType: ControlRoomWorkTabType,
): CarrierType | undefined => {
  let carrierVisitType: CarrierType | undefined

  switch (tabType) {
    case 'vessel':
      carrierVisitType = CarrierType.Vessel
      break
    case 'truck':
      carrierVisitType = CarrierType.Truck
      break
    case 'train':
      carrierVisitType = CarrierType.Train
      break
    case 'internal':
      carrierVisitType = undefined
      break
  }

  return carrierVisitType
}

export const mapCarrierVisitWorkQueueToViewModel = (
  wq: CarrierVisitWorkQueueDto,
  workInstructions: WorkInstructionControlRoomDto[],
): CarrierVisitWorkQueueViewModel => {
  const filteredWorkInstructions = workInstructions.filter(wi => wi.workQueueId === wq.id)

  const statuses = filteredWorkInstructions.map(wi => wi.status!)
  const status = mapStatusToWorkQueueStatus(statuses)

  return {
    ...wq,
    status: status,
    workInstructionsAmount: filteredWorkInstructions.length,
    endTime: calculateEndTime(new Date(wq.startTime), wq.movesAmount, wq.processingTimeSeconds),
  }
}

export const mapCarrierVisitCraneToViewModel = (
  crane: CarrierVisitCraneDto,
  workQueues: CarrierVisitWorkQueueViewModel[],
): CarrierVisitCraneViewModel => {
  const statuses = workQueues.filter(wq => wq.status).map(wq => wq.status!)
  const status = mapStatusToWorkQueueStatus(statuses)

  return {
    ...crane,
    status: status,
    workQueues: sortWorkQueuesByStatus(workQueues),
  }
}

export const mapWorkInstructionToViewModel = (
  workInstruction: WorkInstructionControlRoomDto,
  workQueue?: CarrierVisitWorkQueueViewModel,
  craneName?: string,
): WorkInstructionViewModel => {
  const workQueueFromTime = workQueue ? new Date(workQueue.startTime) : undefined
  const workQueueToTime = workQueue?.endTime
  const plannedTime = workQueue
    ? dayjs(workQueue.startTime)
        .add((workInstruction.workQueueOrder ?? 0) * workQueue.processingTimeSeconds, 'seconds')
        .toDate()
    : undefined

  const lastEquipment = workInstruction.jobs
    ?.filter(job => job.executionTime)
    .sort(
      (a, b) => new Date(b.executionTime!).getTime() - new Date(a.executionTime!).getTime(),
    )[0]?.equipmentName

  return {
    ...workInstruction,
    workQueueFromTime,
    workQueueToTime,
    workQueueName: workQueue?.name,
    lastEquipment: lastEquipment,
    plannedTime,
    craneName,
  }
}

export const mapWorkInstructionUpsertedEventToViewModel = (
  workInstructionUpsertedEvent: WorkInstructionUpsertedEventPayload,
  workQueue?: CarrierVisitWorkQueueViewModel,
  craneName?: string,
  jobs?: WorkInstructionControlRoomJobDto[],
): WorkInstructionControlRoomDto => {
  const wiControlRoom: WorkInstructionControlRoomDto = {
    id: workInstructionUpsertedEvent.workInstruction.id,
    workQueueId: workInstructionUpsertedEvent.workInstruction.workQueueId,
    carrierVisitId: workInstructionUpsertedEvent.workInstruction.carrierVisitId,
    status: workInstructionUpsertedEvent.workInstruction.status,
    workQueueOrder: workInstructionUpsertedEvent.workInstruction.workQueueOrder,
    operationType: workInstructionUpsertedEvent.workInstruction.operationType,
    destinationDescription: workInstructionUpsertedEvent.workInstruction.destination,
    originDescription: workInstructionUpsertedEvent.workInstruction.origin,
    bay: workInstructionUpsertedEvent.workInstruction.bay,
    executionTime: workInstructionUpsertedEvent.workInstruction.finishedAt,
    isApproved: workInstructionUpsertedEvent.workInstruction.isApproved,
    journeyCategory: workInstructionUpsertedEvent.workInstruction.cargoUnit?.category,
    transportUnitNumber: workInstructionUpsertedEvent.workInstruction.cargoUnit?.containerNumber,
    vesselArea: workInstructionUpsertedEvent.workInstruction.vesselArea,
    hasActiveHolds: workInstructionUpsertedEvent.workInstruction.hasActiveHolds,
    isPriority: workInstructionUpsertedEvent.workInstruction.isPriority,
    jobs: [],
  }

  if (!workInstructionUpsertedEvent.clearJobList && workInstructionUpsertedEvent.newJob) {
    const from = mapLocationToControlRoom(workInstructionUpsertedEvent.newJob.from)
    const to = mapLocationToControlRoom(workInstructionUpsertedEvent.newJob.to)

    const newJobMapped: WorkInstructionControlRoomJobDto = {
      id: workInstructionUpsertedEvent.newJob.id,
      from: from,
      to: to,
      equipmentId: workInstructionUpsertedEvent.newJob.equipmentId,
      equipmentName: workInstructionUpsertedEvent.newJob.equipmentName,
      executionTime: workInstructionUpsertedEvent.newJob.created,
      fromDescription: from.locationName ?? '',
      toDescription: to.locationName ?? '',
    }

    if (jobs?.some(job => job.id === newJobMapped.id)) {
      wiControlRoom.jobs = map(jobs, job => (job.id === newJobMapped.id ? newJobMapped : job))
    } else {
      wiControlRoom.jobs = [...(jobs ?? []), newJobMapped]
    }
  }

  return mapWorkInstructionToViewModel(wiControlRoom, workQueue, craneName)
}

export const sortWorkQueuesByStatus = (
  workQueues: CarrierVisitWorkQueueViewModel[],
): CarrierVisitWorkQueueViewModel[] => {
  const statusOrder: Record<string, number> = {
    [WorkQueueStatus.InProgress]: 0,
    [WorkQueueStatus.Stopped]: 1,
    [WorkQueueStatus.Todo]: 2,
    [WorkQueueStatus.Pending]: 3,
    [WorkQueueStatus.Finished]: 4,
  }

  const sortedWorkQueues = [...workQueues].sort((a, b) => {
    const statusDiff =
      statusOrder[a.status ?? WorkQueueStatus.Pending] -
      statusOrder[b.status ?? WorkQueueStatus.Pending]
    if (statusDiff !== 0) return statusDiff

    return a.order - b.order
  })

  return sortedWorkQueues
}

export const sortCarrierVisitsByStatusAndName = (
  carrierVisits: CarrierVisitViewModel[],
): CarrierVisitViewModel[] => {
  const statusOrder: Record<CarrierVisitStatus, number> = {
    InOperation: 0,
    Arrived: 1,
    Completed: 2,
    Expected: 3,
    Departed: 4,
    Closed: 5,
    Cancelled: 6,
  }

  return [...carrierVisits].sort((a, b) => {
    const statusDiff =
      statusOrder[a.status as keyof typeof statusOrder] -
      statusOrder[b.status as keyof typeof statusOrder]
    if (statusDiff !== 0) return statusDiff

    return (a.carrierName ?? '').localeCompare(b.carrierName ?? '')
  })
}

export const mapLocationToControlRoom = (location: LocationDto): Location => {
  return {
    type: location.type,
    locationName: location.locationName,
    yardBlockName: location.yardBlockName,
    yardBlockType: location.yardBlockType,
    yardBays: location.yardBays,
    equipmentId: location.equipmentId,
  }
}

export const mapStatusToWorkQueueStatus = (
  statuses: WorkInstructionStatus[] | WorkQueueStatus[],
): WorkQueueStatus | undefined => {
  let status: WorkQueueStatus | undefined

  if (!statuses.length || statuses.every(s => s === undefined)) {
    status = undefined
  } else if (statuses.every(s => s === WorkInstructionStatus.Pending)) {
    status = WorkQueueStatus.Pending
  } else if (statuses.every(s => s === WorkInstructionStatus.Todo)) {
    status = WorkQueueStatus.Todo
  } else if (statuses.every(s => s === WorkInstructionStatus.Finished)) {
    status = WorkQueueStatus.Finished
  } else if (
    statuses.every(s => s === WorkInstructionStatus.Stopped || s === WorkInstructionStatus.Finished)
  ) {
    status = WorkQueueStatus.Stopped
  } else {
    status = WorkQueueStatus.InProgress
  }

  return status
}
